import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

function fn(name: string) {
  return httpsCallable(getFunctions(), name);
}

export default function OfferAcceptPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => String(params.get('token') || '').trim(), [params]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offer, setOffer] = useState<any>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError('Missing offer token. Open the link from your hire email.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res: any = await fn('hiringGetOffer')({ token });
        if (!cancelled) {
          setOffer(res.data);
          setAccepted(Boolean(res.data?.offerAcceptedAt));
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load this offer.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    if (!token) return;
    setAccepting(true);
    setError(null);
    try {
      const res: any = await fn('hiringAcceptOffer')({ token });
      setOffer(res.data);
      setAccepted(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to accept offer.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #e3f2fd 0%, #f8fafc 40%, #fff 100%)',
        padding: '32px 16px',
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(2,136,209,0.12)',
          padding: 28,
        }}
      >
        <div style={{ fontSize: 13, letterSpacing: 0.04, color: '#0288D1', fontWeight: 700 }}>
          OVERTURE CAREERS
        </div>
        <h1 style={{ margin: '8px 0 0', fontSize: 28 }}>Your offer</h1>

        {loading && <p style={{ marginTop: 16 }}>Loading offer…</p>}
        {error && (
          <div style={{ marginTop: 16, padding: 12, borderRadius: 10, background: '#fef2f2', color: '#991b1b' }}>
            {error}
          </div>
        )}

        {!loading && offer && (
          <>
            <p style={{ marginTop: 12, fontSize: 16, lineHeight: 1.5 }}>
              Congratulations{offer.displayName ? `, ${String(offer.displayName).split(' ')[0]}` : ''}!
              We are pleased to offer you the <strong>{offer.roleLabel}</strong> position
              {offer.locationLabel ? (
                <>
                  , based in <strong>{offer.locationLabel}</strong>
                </>
              ) : null}
              .
            </p>

            <div style={{ marginTop: 16, display: 'grid', gap: 8, fontSize: 14 }}>
              {offer.startDate && (
                <div>
                  <strong>Start date:</strong> {offer.startDate}
                </div>
              )}
              {offer.hourlyRate && (
                <div>
                  <strong>Rate:</strong> ${offer.hourlyRate}/hour
                </div>
              )}
            </div>

            {offer.offerPdfUrl && (
              <p style={{ marginTop: 16 }}>
                <a href={offer.offerPdfUrl} target="_blank" rel="noreferrer">
                  View / download offer letter (PDF)
                </a>
              </p>
            )}

            {accepted ? (
              <div
                style={{
                  marginTop: 20,
                  padding: 16,
                  borderRadius: 12,
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  color: '#065f46',
                }}
              >
                <strong>Offer accepted — welcome to the team!</strong>
                <p style={{ margin: '8px 0 0' }}>
                  Check your email for onboarding instructions. Log in to the web-app to complete your
                  certification checklist (starting with your employment contract).
                </p>
                <Link
                  to="/app/onboarding"
                  style={{
                    display: 'inline-block',
                    marginTop: 12,
                    background: '#0288D1',
                    color: '#fff',
                    textDecoration: 'none',
                    padding: '10px 14px',
                    borderRadius: 10,
                    fontWeight: 700,
                  }}
                >
                  Go to onboarding checklist →
                </Link>
              </div>
            ) : (
              <button
                type="button"
                onClick={accept}
                disabled={accepting}
                style={{
                  marginTop: 20,
                  background: '#0288D1',
                  color: '#fff',
                  border: 0,
                  borderRadius: 10,
                  padding: '12px 18px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                {accepting ? 'Accepting…' : 'Accept offer'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
